

import Component from "vue-class-component";
import {Prop, Vue} from "vue-property-decorator";
import * as icons from "@fortawesome/free-solid-svg-icons";

@Component({})
export default class DownloadButton extends Vue {
    icons = icons;

    /** Absolute URL of the file (what the API stores in document.file). */
    @Prop({default: ""})
    url!: string;

    /** Human-readable name, used for the saved filename. Usually the document title. */
    @Prop({default: ""})
    name!: string;

    @Prop({default: false})
    withLabel!: boolean;

    /** "tile" for the v1 layout, "card" for the overlay buttons in v2. */
    @Prop({default: "tile"})
    variant!: string;

    busy = false;

    get buttonClass(): string {
        return this.variant === "card"
            ? "portal-action"
            : "btn btn-sm btn-outline-secondary";
    }

    /**
     * Prefer the document title for the saved filename, since the stored name is
     * a uuid-prefixed blob. Keep whatever extension the URL carries.
     */
    get filename(): string {
        const fromUrl = (this.url.split("/").pop() ?? "download").split("?")[0];
        const ext = fromUrl.includes(".") ? fromUrl.substring(fromUrl.lastIndexOf(".")) : "";

        if (!this.name) {
            return decodeURIComponent(fromUrl);
        }

        const base = this.name.replace(/[\\/:*?"<>|]+/g, "-").trim();
        return base.toLowerCase().endsWith(ext.toLowerCase()) ? base : base + ext;
    }

    async download() {
        if (!this.url) return;

        this.busy = true;
        try {
            // Fetching to a blob is what makes the browser save instead of navigate.
            // A plain <a download> is ignored when the file is on another origin.
            const response = await fetch(this.url, {mode: "cors", credentials: "omit"});
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            this.triggerAnchor(objectUrl, this.filename);
            setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
        } catch (e) {
            // The file host has no CORS headers. Fall back to a direct link — it will
            // still save for same-origin files and open in a tab otherwise.
            this.triggerAnchor(this.url, this.filename);
        } finally {
            this.busy = false;
        }
    }

    private triggerAnchor(href: string, filename: string) {
        const anchor = document.createElement("a");
        anchor.href = href;
        anchor.download = filename;
        anchor.rel = "noopener";
        anchor.style.display = "none";
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
    }
}

